/**
 * PhotoViewer
 *
 * A simple image viewer with pinch-to-zoom and drag-to-move functionality.
 * Uses Hammer.js for mobile events (js needs to be added to pages individually).
 * This code is used for the QRSW team photo modal:
 * wp-content/themes/streetsmarts/template-parts/blocks/qrsw/2024/billy-slater-billboard-hero--phase3.php
 */

class PhotoViewer {
  constructor(selector) {
    this.body = document.querySelector("body");
    this.viewport = document.querySelector(selector);

    if (!this.viewport) {
      console.error("PhotoViewer", "Viewport not found");
      return;
    }

    this.imageSrc = this.viewport.getAttribute("data-src");
    this.imageAlt = this.viewport.getAttribute("data-alt") || "";

    if (!this.imageSrc) {
      console.error(
        "PhotoViewer",
        "Image source not found! Add 'data-src' attribute to the viewport element."
      );
      return;
    }

    this.mc;
    this.image;
    this.offsetX = 0;
    this.offsetY = 0;
    this.panStartX;
    this.panStartY;
    this.viewportWidth;
    this.viewportHeight;
    this.createImage();
  }

  createImage() {
    this.image = document.createElement("img");
    this.image.src = this.imageSrc;
    this.image.alt = this.imageAlt;
    this.image.style.opacity = 0;
    this.image.setAttribute("draggable", "false");
    this.image.addEventListener("load", () => {
      this.start();
    });
    this.viewport.appendChild(this.image);
  }

  start() {
    this.onResize();
    this.initHammer();
    this.addEventListeners();
    this.image.style.opacity = 1;
  }

  initHammer() {
    this.mc = new Hammer.Manager(this.image);
    this.mc.add(new Hammer.Pan({ threshold: 0, pointers: 0 }));
    this.mc.add(new Hammer.Swipe()).recognizeWith(this.mc.get("pan"));
    this.mc
      .add(new Hammer.Pinch({ threshold: 0 }))
      .recognizeWith([this.mc.get("pan")]);

    /* other options: */

    this.mc.add(new Hammer.Tap({ event: "doubletap", taps: 2 }));
    // this.mc.add(new Hammer.Tap());

    // this.mc.on("swipe", (e) => this.onSwipe(e));
    //this. mc.on("tap", (e) => this.onTap(e));
  }

  addEventListeners() {
    window.addEventListener("resize", () => this.onResize());
    this.viewport.addEventListener("wheel", (e) => this.onMouseWheel(e));
    // this.image.addEventListener("keydown", (e) => this.onKeyDown(e));
    /* mobile events */
    this.mc.on("panstart panmove", (e) => this.onPan(e));
    this.mc.on("pinchstart pinchmove", (e) => this.onPinch(e));
    this.mc.on("doubletap", (e) => this.onDoubleTap(e));
  }

  removeEventListeners() {
    window.removeEventListener("resize", () => this.onResize());
    this.image.removeEventListener("wheel", (e) => this.onMouseWheel(e));
    // this.image.removeEventListener("keydown", (e) => this.onKeyDown(e));
    /* mobile events */
    this.mc.off("panstart panmove", (e) => this.onPan(e));
    this.mc.off("pinchstart pinchmove", (e) => this.onPinch(e));
  }

  onResize() {
    this.viewportWidth = this.viewport.offsetWidth;
    this.viewportHeight = this.viewport.offsetHeight;

    this.resizeImage({
      width: this.viewportWidth,
      height: this.viewportHeight,
    });
  }

  /* Mobile Events (Hammer.js) */

  onPinch(e) {
    this.resizeImage({
      width: this.image.offsetWidth * e.scale * 0.5,
      height: this.image.offsetHeight * e.scale * 0.5,
    });
  }

  onPanStart() {
    this.panStartX = this.image.offsetLeft;
    this.panStartY = this.image.offsetTop;
  }

  onPanMove(e) {
    this.moveImage({
      x: this.panStartX + e.deltaX,
      y: this.panStartY + e.deltaY,
    });
  }

  onPan(e) {
    if (e.type == "panstart") {
      this.onPanStart();
    } else {
      this.onPanMove(e);
    }
  }

  onSwipe(e) {
    console.log("swipe", e.type);
  }

  onTap(e) {
    console.log("tap", e.type);
  }

  onDoubleTap(e) {
    this.resizeImage({
      width: this.image.naturalWidth,
      height: this.image.naturalHeight,
    });
  }

  /* Desktop Events */

  onMouseWheel(e) {
    e.preventDefault();
    const factor = e.deltaY * 5;

    this.resizeImage({
      width: this.image.offsetWidth + factor,
      height: this.image.offsetHeight + factor,
    });
  }

  /* Image Manipulation */

  resizeImage(size) {
    const maxWidth = this.image.naturalWidth;
    const maxHeight = this.image.naturalHeight;
    size.width = size.width || 0;
    size.height = size.height || 0;

    this.image.style.height =
      Math.min(maxHeight, Math.max(this.viewportHeight, size.height)) + "px";
    this.image.style.width = "auto";

    if (this.image.offsetWidth > this.viewportWidth) {
      this.image.style.width =
        Math.min(maxWidth, Math.max(this.viewportWidth, size.width)) + "px";
      this.image.style.height = "auto";
    }

    this.applyBounds();
  }

  moveImage(pos) {
    if (pos.x) {
      this.image.style.left = pos.x + "px";
    }

    if (pos.y) {
      this.image.style.top = pos.y + "px";
    }

    this.applyBounds();
  }

  /* Contain image within viewport bounds */

  applyBounds() {
    let x = this.image.offsetLeft;
    let y = this.image.offsetTop;
    const centerX = this.viewportWidth * 0.5;
    const centerY = this.viewportHeight * 0.5;
    const boundsX = (this.image.offsetWidth - this.viewportWidth) * 0.5;
    const boundsY = (this.image.offsetHeight - this.viewportHeight) * 0.5;

    if (this.image.offsetWidth <= this.viewportWidth) {
      x = centerX;
    } else {
      if (x < centerX) {
        x = Math.max(centerX - boundsX, x);
      } else {
        x = Math.min(centerX + boundsX, x);
      }
    }

    if (this.image.offsetHeight <= this.viewportHeight) {
      y = centerY;
    } else {
      if (y < centerY) {
        y = Math.max(centerY - boundsY, y);
      } else {
        y = Math.min(centerY + boundsY, y);
      }
    }

    this.image.style.left = x + "px";
    this.image.style.top = y + "px";
  }
}

/* Export */

window.streetsmarts = window.streetsmarts || {};
window.streetsmarts.PhotoViewer = PhotoViewer;
